import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Cloud, ShieldCheck, AlertTriangle, Trash2, Radar } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
    {children}
  </div>
);

export default function CloudSettingsPage() {
  const [options, setOptions] = useState(null);
  const [adapters, setAdapters] = useState(null);
  const [form, setForm] = useState({ provider: "", region: "", host: "", port: "", database: "", username: "", credentials: "", acknowledged_non_gcc: false });
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(null);

  const load = () => api.get("/cloud/adapters").then((r) => setAdapters(r.data.adapters));
  useEffect(() => {
    api.get("/cloud/options").then((r) => setOptions(r.data));
    load();
  }, []);

  if (!options || adapters === null) return <Skeleton className="h-96 w-full rounded-none" />;

  const provider = options.providers.find((p) => p.id === form.provider);
  const region = provider?.regions.find((r) => r.id === form.region);
  const needsAck = region && !region.sovereign;

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/cloud/adapters", { ...form, port: Number(form.port), credentials: form.credentials || null });
      toast.success("Adapter saved — credentials encrypted at rest (Fernet)");
      setForm({ provider: "", region: "", host: "", port: "", database: "", username: "", credentials: "", acknowledged_non_gcc: false });
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const test = async (id) => {
    setTesting(id);
    try {
      await api.post(`/cloud/adapters/${id}/test`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTesting(null); }
  };

  const remove = async (id) => {
    await api.delete(`/cloud/adapters/${id}`);
    toast.success("Adapter removed");
    await load();
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 fade-in items-start" data-testid="cloud-settings-page">
      <div className="bg-white border border-border" data-testid="adapter-form">
        <div className="px-5 py-3 border-b border-border flex items-center gap-2">
          <Cloud className="h-4 w-4 text-teal-600" />
          <p className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[var(--brand-primary)]">New Enterprise Database Adapter</p>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Provider">
              <Select value={form.provider}
                onValueChange={(v) => setForm({ ...form, provider: v, region: "", port: String(options.providers.find((p) => p.id === v)?.default_port || "") })}>
                <SelectTrigger className="rounded-none" data-testid="adapter-provider-select"><SelectValue placeholder="Select provider" /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {options.providers.map((p) => <SelectItem key={p.id} value={p.id} className="text-[12px]">{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Region">
              <Select value={form.region} onValueChange={(v) => setForm({ ...form, region: v })} disabled={!provider}>
                <SelectTrigger className="rounded-none" data-testid="adapter-region-select"><SelectValue placeholder="Select region" /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {(provider?.regions || []).map((r) => (
                    <SelectItem key={r.id} value={r.id} className="text-[12px]">
                      {r.label} {r.sovereign ? "· GCC" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          {region && (region.sovereign ? (
            <div className="flex items-start gap-2.5 bg-teal-50 border border-teal-600/30 px-3 py-2.5" data-testid="sovereignty-compliant-badge">
              <ShieldCheck className="h-4 w-4 text-teal-700 shrink-0 mt-0.5" />
              <div>
                <p className="text-[12px] font-semibold text-teal-800">Data sovereignty: COMPLIANT — Qatar/GCC region</p>
                <p className="text-[11px] text-teal-800/80 mt-0.5">{options.sovereign_reference}</p>
              </div>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-400 px-3 py-2.5 space-y-2" data-testid="sovereignty-warning">
              <div className="flex items-start gap-2.5">
                <AlertTriangle className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[12px] font-semibold text-amber-900">Sovereignty warning — region outside Qatar/GCC</p>
                  <p className="text-[11px] text-amber-800 mt-0.5">{options.non_gcc_warning}</p>
                </div>
              </div>
              <label className="flex items-center gap-2 pl-6 cursor-pointer">
                <Checkbox data-testid="sovereignty-ack-checkbox" checked={form.acknowledged_non_gcc}
                  onCheckedChange={(v) => setForm({ ...form, acknowledged_non_gcc: !!v })} className="rounded-none" />
                <span className="text-[11.5px] text-amber-900">I acknowledge data will reside outside the Qatar/GCC sovereignty zone.</span>
              </label>
            </div>
          ))}

          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <Field label="Host">
                <Input data-testid="adapter-host-input" value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })}
                  placeholder="db.qatarcentral.example.net" className="rounded-none font-mono text-[12px]" />
              </Field>
            </div>
            <Field label="Port">
              <Input data-testid="adapter-port-input" type="number" value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })}
                className="rounded-none font-mono text-[12px]" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Database">
              <Input data-testid="adapter-db-input" value={form.database} onChange={(e) => setForm({ ...form, database: e.target.value })}
                className="rounded-none font-mono text-[12px]" />
            </Field>
            <Field label="Username">
              <Input data-testid="adapter-user-input" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
                className="rounded-none font-mono text-[12px]" />
            </Field>
          </div>
          <Field label="Credentials (write-only — encrypted at rest, never displayed again)">
            <PasswordInput data-testid="adapter-credentials-input" value={form.credentials}
              onChange={(e) => setForm({ ...form, credentials: e.target.value })}
              placeholder="••••••••" className="rounded-none font-mono text-[12px]" />
          </Field>
          <div className="flex justify-end">
            <Button data-testid="adapter-save-btn" onClick={save}
              disabled={busy || !form.provider || !form.region || !form.host || !form.port || !form.database || !form.username || (needsAck && !form.acknowledged_non_gcc)}
              className="rounded-none h-9 text-[12px] bg-teal-600 hover:bg-teal-700">
              {busy ? "Saving…" : "Save adapter"}
            </Button>
          </div>
        </div>
      </div>

      <div className="space-y-4" data-testid="adapters-list">
        <p className="text-[12px] text-muted-foreground">
          Configured adapters feed the data-residency indicator in the Security Console posture panel.
          "Test connection" is an honest <span className="font-semibold text-slate-700">network reachability check</span> (TCP, 4s timeout) — results reported verbatim.
        </p>
        {adapters.length === 0 && (
          <div className="bg-white border border-border px-4 py-10 text-center text-[13px] text-muted-foreground">
            No adapters configured yet.
          </div>
        )}
        {adapters.map((a) => (
          <div key={a.id} className="bg-white border border-border" data-testid={`adapter-card-${a.id}`}>
            <div className="px-4 py-3 border-b border-border flex items-center gap-3">
              <div className="flex-1">
                <p className="text-[13px] font-semibold text-[var(--brand-primary)]">{a.provider_label}</p>
                <p className="text-[11px] font-mono text-muted-foreground">{a.host}:{a.port} / {a.database} · user {a.username}</p>
              </div>
              {a.sovereign ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-mono bg-teal-600 text-white px-2 py-1">
                  <ShieldCheck className="h-3 w-3" /> {a.region_label} · GCC COMPLIANT
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] font-mono bg-amber-500 text-white px-2 py-1">
                  <AlertTriangle className="h-3 w-3" /> {a.region_label} · NON-GCC
                </span>
              )}
            </div>
            <div className="px-4 py-3 space-y-2.5">
              <p className="text-[11px] font-mono text-slate-500">
                credentials: {a.credentials_set ? a.credentials_masked + " (encrypted at rest)" : "not set"}
              </p>
              {a.last_test && (
                <div className={`text-[11.5px] px-3 py-2 border ${a.last_test.reachable ? "bg-teal-50 border-teal-600/30 text-teal-900" : "bg-slate-50 border-border text-slate-700"}`}
                  data-testid={`adapter-test-result-${a.id}`}>
                  <span className="font-mono text-[10px] uppercase mr-2">{a.last_test.check} · {a.last_test.reachable ? "REACHABLE" : "UNREACHABLE"}</span>
                  {a.last_test.detail}
                </div>
              )}
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" data-testid={`adapter-test-btn-${a.id}`}
                  onClick={() => test(a.id)} disabled={testing === a.id}
                  className="rounded-none h-8 text-[11px] border-teal-600/50 text-teal-700 hover:bg-teal-50">
                  <Radar className="h-3.5 w-3.5" /> {testing === a.id ? "Checking…" : "Network reachability check"}
                </Button>
                <Button size="sm" variant="outline" data-testid={`adapter-delete-btn-${a.id}`}
                  onClick={() => remove(a.id)}
                  className="rounded-none h-8 text-[11px] text-red-700 border-red-300 hover:bg-red-50">
                  <Trash2 className="h-3.5 w-3.5" /> Remove
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
